import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatCurrency, WHOLE_DOLLARS } from '@/lib/formatters'
import { CoverageType } from '@/models/insuranceCoverage'
import {
  type PremiumBreakdownTotals,
  PremiumBucket,
  type PremiumLineTotal,
  type PremiumLineType,
} from '@/models/salesAnalytics'
import { PremiumFigure } from './PremiumFigure'

// The buckets stay grey: only a coverage somebody can name gets a color.
export const LINE_COLORS: Record<PremiumLineType, string> = {
  [CoverageType.AUTO_LIABILITY]: 'bg-emerald-500',
  [CoverageType.AUTO_PHYSICAL_DAMAGE]: 'bg-sky-500',
  [CoverageType.GENERAL_LIABILITY]: 'bg-violet-500',
  [CoverageType.MOTOR_TRUCK_CARGO]: 'bg-amber-500',
  [CoverageType.WORKERS_COMPENSATION]: 'bg-rose-500',
  [CoverageType.PET]: 'bg-teal-500',
  [PremiumBucket.OTHER]: 'bg-neutral-300',
  [PremiumBucket.FEES]: 'bg-neutral-400',
}

interface Props {
  lines: PremiumLineTotal[]
  totals: PremiumBreakdownTotals
}

export function PremiumByLineCard({ lines, totals }: Props) {
  const peak = Math.max(...lines.map((line) => line.premium), 1)
  const unpricedPolicies = totals.policiesWithoutPricedCoverage

  return (
    <Card>
      <CardHeader>
        <CardTitle>Premium by coverage</CardTitle>
        <p className="text-sm text-neutral-500">
          The policies in the table above, split into the lines that earn their premium. A policy bundling
          several coverages with one carrier is a single row everywhere else on this page.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-6">
          <div>
            <div className="text-2xl tabular-nums">{formatCurrency(totals.premium, WHOLE_DOLLARS)}</div>
            <div className="text-xs text-neutral-500">Total premium</div>
          </div>
          <div>
            <div className="text-2xl tabular-nums">{totals.coverageLines}</div>
            <div className="text-xs text-neutral-500">Coverage lines</div>
          </div>
          <div>
            <div className="text-2xl tabular-nums">{totals.policies}</div>
            <div className="text-xs text-neutral-500">Policies</div>
          </div>
          <div>
            <div className="text-2xl tabular-nums">{formatCurrency(totals.fees, WHOLE_DOLLARS)}</div>
            <div className="text-xs text-neutral-500">Taxes &amp; fees</div>
          </div>
        </div>

        {totals.bundledPolicies > 0 && (
          <p className="text-sm text-neutral-500">
            {totals.bundledPolicies} of {totals.policies} {totals.policies === 1 ? 'policy' : 'policies'}{' '}
            {totals.bundledPolicies === 1 ? 'bundles' : 'bundle'} more than one coverage but{' '}
            {totals.bundledPolicies === 1 ? 'shows' : 'show'} as a single row in the tables above.
          </p>
        )}

        {unpricedPolicies > 0 && (
          <p className="text-sm text-amber-700 dark:text-amber-400">
            {unpricedPolicies} bound {unpricedPolicies === 1 ? 'policy has' : 'policies have'} no priced
            coverage, so {unpricedPolicies === 1 ? 'its' : 'their'} coverage premium is missing from the
            figures below.
          </p>
        )}

        {totals.policies === 0 && <div className="text-sm text-neutral-500">Nothing in range.</div>}

        {lines.map((line) => (
          <div key={line.lineType} className="grid grid-cols-[9rem_1fr_auto] items-center gap-3">
            <div className="text-sm text-neutral-500">{line.label}</div>
            <div className="h-6 overflow-hidden rounded bg-neutral-100 dark:bg-neutral-800">
              <div
                className={`h-full rounded ${LINE_COLORS[line.lineType]}`}
                style={{ width: `${(line.premium / peak) * 100}%` }}
              />
            </div>
            <div className="text-right text-sm whitespace-nowrap tabular-nums">
              <PremiumFigure premium={line.premium} lines={line.lines} unpricedLines={line.unpricedLines} />
              <span className="ml-2 text-neutral-500">
                {line.unpricedLines < line.lines && `${(line.share * 100).toFixed(0)}% · `}
                {line.lines} {line.lines === 1 ? 'line' : 'lines'}
              </span>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
